import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import * as Papa from 'papaparse';
import pdfMake from 'pdfmake/build/pdfmake';
import { vfs } from '../fonts/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { Competition, EventReferee, Result, Champion, Participant } from '../types/models';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import QRCode from 'qrcode';

// Initialize pdfmake with virtual font files
pdfMake.vfs = vfs;

// Configure fonts for pdfmake
pdfMake.fonts = {
  NotoSansTamil: {
    normal: 'NotoSansTamil-Regular.ttf',
    bold: 'NotoSansTamil-Bold.ttf',
    italics: 'NotoSansTamil-Regular.ttf',
    bolditalics: 'NotoSansTamil-Bold.ttf'
  },
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Bold.ttf',
    italics: 'Roboto-Regular.ttf',
    bolditalics: 'Roboto-Bold.ttf'
  }
};

// Function to generate QR code as data URL
const generateQRCode = async (text: string): Promise<string> => {
  try {
    const qrDataUrl = await QRCode.toDataURL(text, {
      width: 200,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#ffffff'
      }
    });
    return qrDataUrl;
  } catch (err) {
    console.error('Error generating QR code:', err);
    return '';
  }
};

// Function to get school logo URL from system settings
const getSchoolLogo = async (): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('system_settings')
      .select('setting_value')
      .eq('setting_name', 'school_logo')
      .single();
    
    if (error || !data || !data.setting_value?.url) return null;
    
    return data.setting_value.url;
  } catch (error) {
    console.error('Error fetching school logo:', error);
    return null;
  }
};

export const exportToExcel = (data: any[]) => {
  // For participants, transform the data to include all requested fields
  if (data[0]?.student_id) { // Check if it's participant data
    const formattedData = data.map(participant => ({
      'First Name': participant.first_name,
      'Last Name': participant.last_name,
      'Tamil First Name': participant.first_name_tamil || '',
      'Tamil Last Name': participant.last_name_tamil || '',
      'Date of Birth': format(new Date(participant.date_of_birth), 'yyyy-MM-dd'),
      'Gender': participant.gender === 'M' ? 'Male' : 'Female',
      'House': participant.house?.house_name || '',
      'House Color': participant.house?.house_color || '',
      'Department': participant.department?.name || '',
      'Reference Number': participant.reference_number || '',
      'Payment Status': participant.payment_status || 'not_paid',
      'Payment Date': participant.payment_date ? format(new Date(participant.payment_date), 'yyyy-MM-dd') : '',
      'Payment Notes': participant.payment_notes || ''
    }));
    data = formattedData;
  }

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(dataBlob, 'export.xlsx');
};

export const exportToCSV = (data: any[]) => {
  // For participants, transform the data to include all requested fields
  if (data[0]?.student_id) { // Check if it's participant data
    const formattedData = data.map(participant => ({
      'First Name': participant.first_name,
      'Last Name': participant.last_name,
      'Tamil First Name': participant.first_name_tamil || '',
      'Tamil Last Name': participant.last_name_tamil || '',
      'Date of Birth': format(new Date(participant.date_of_birth), 'yyyy-MM-dd'),
      'Gender': participant.gender === 'M' ? 'Male' : 'Female',
      'House': participant.house?.house_name || '',
      'House Color': participant.house?.house_color || '',
      'Department': participant.department?.name || '',
      'Reference Number': participant.reference_number || '',
      'Payment Status': participant.payment_status || 'not_paid',
      'Payment Date': participant.payment_date ? format(new Date(participant.payment_date), 'yyyy-MM-dd') : '',
      'Payment Notes': participant.payment_notes || ''
    }));
    data = formattedData;
  }

  const csv = Papa.unparse(data);
  const csvBlob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(csvBlob, 'export.csv');
};

export const exportToPDF = async (data: any[]) => {
  try {
    // Get school logo if available
    const logoUrl = await getSchoolLogo();
    
    let logoImage = null;
    if (logoUrl) {
      try {
        // Fetch the image and convert to base64
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        logoImage = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error loading logo image:', error);
      }
    }
    
    // For participants, create a custom PDF layout
    if (data[0]?.student_id) {
      const docDefinition: TDocumentDefinitions = {
        pageOrientation: 'landscape',
        footer: function(currentPage, pageCount) {
          return {
            text: `Side ${currentPage} av ${pageCount}`,
            alignment: 'right',
            margin: [0, 0, 40, 0]
          };
        },
        content: [
          {
            columns: [
              // Left column for logo
              {
                width: 100,
                stack: logoImage ? [
                  {
                    image: logoImage as string,
                    width: 80,
                    alignment: 'left'
                  }
                ] : [],
                margin: [0, 0, 10, 0]
              },
              // Right column for title
              {
                width: '*',
                text: { text: 'பங்கேற்பாளர்கள் பட்டியல்', font: 'NotoSansTamil' },
                style: 'header',
                alignment: 'center'
              },
              // Empty column for balance
              {
                width: 100,
                text: ''
              }
            ]
          },
          {
            table: {
              headerRows: 1,
              widths: ['*', '*', '*', 'auto', 'auto', '*', 'auto', 'auto', '*'],
              body: [
                [
                  { text: 'பெயர் (ஆங்கிலம்)', font: 'NotoSansTamil', bold: true },
                  { text: 'பெயர் (தமிழ்)', font: 'NotoSansTamil', bold: true },
                  { text: 'பிறந்த தேதி', font: 'NotoSansTamil', bold: true },
                  { text: 'பாலினம்', font: 'NotoSansTamil', bold: true },
                  { text: 'இல்லம்', font: 'NotoSansTamil', bold: true },
                  { text: 'வளாகம்', font: 'NotoSansTamil', bold: true },
                  { text: 'குறிப்பு எண்', font: 'NotoSansTamil', bold: true },
                  { text: 'கட்டண நிலை', font: 'NotoSansTamil', bold: true },
                  { text: 'குறிப்புகள்', font: 'NotoSansTamil', bold: true }
                ],
                ...data.map(participant => [
                  `${participant.first_name} ${participant.last_name}`,
                  {
                    text: participant.first_name_tamil && participant.last_name_tamil ?
                      `${participant.first_name_tamil} ${participant.last_name_tamil}` : '-',
                    font: 'NotoSansTamil'
                  },
                  format(new Date(participant.date_of_birth), 'dd.MM.yyyy'),
                  {
                    text: participant.gender === 'M' ? 'ஆண்' : 'பெண்',
                    font: 'NotoSansTamil'
                  },
                  {
                    text: participant.house?.house_name || '-',
                    font: 'NotoSansTamil'
                  },
                  {
                    text: participant.department?.name_tamil || participant.department?.name || '-',
                    font: participant.department?.name_tamil ? 'NotoSansTamil' : 'Roboto'
                  },
                  participant.reference_number || '-',
                  {
                    text: participant.payment_status === 'paid' ? 'செலுத்தப்பட்டது' :
                      participant.payment_status === 'school_paid' ? 'பள்ளி செலுத்தியது' : 
                      'செலுத்தப்படவில்லை',
                    font: 'NotoSansTamil'
                  },
                  {
                    text: participant.payment_notes || '-',
                    font: 'NotoSansTamil'
                  }
                ])
              ]
            }
          }
        ],
        defaultStyle: {
          font: 'NotoSansTamil'
        },
        styles: {
          header: {
            fontSize: 18,
            bold: true,
            margin: [0, 0, 0, 20]
          }
        }
      };

      pdfMake.createPdf(docDefinition).download('participants.pdf');
      return;
    }

    // Default PDF export for other data types
    const docDefinition: TDocumentDefinitions = {
      pageOrientation: 'landscape',
      footer: function(currentPage, pageCount) {
        return {
          text: `Side ${currentPage} av ${pageCount}`,
          alignment: 'right',
          margin: [0, 0, 40, 0]
        };
      },
      content: [
        {
          columns: [
            // Left column for logo
            {
              width: 100,
              stack: logoImage ? [
                {
                  image: logoImage as string,
                  width: 80,
                  alignment: 'left'
                }
              ] : [],
              margin: [0, 0, 10, 0]
            },
            // Right column for title
            {
              width: '*',
              text: 'Export Data',
              style: 'header',
              alignment: 'center'
            },
            // Empty column for balance
            {
              width: 100,
              text: ''
            }
          ]
        },
        {
          table: {
            headerRows: 1,
            body: [
              Object.keys(data[0]),
              ...data.map(item => Object.values(item))
            ]
          }
        }
      ],
      defaultStyle: {
        font: 'NotoSansTamil'
      },
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          margin: [0, 0, 0, 10]
        }
      }
    };

    pdfMake.createPdf(docDefinition).download('export.pdf');
  } catch (error) {
    console.error('Error generating PDF:', error);
  }
};

export const exportChampionsToExcel = (champions: Champion[]) => {
  const data = champions.map(champion => ({
    'Name': `${champion.student?.first_name} ${champion.student?.last_name}`,
    'Tamil Name': `${champion.student?.first_name_tamil || ''} ${champion.student?.last_name_tamil || ''}`,
    'House': champion.student?.house?.house_name,
    'House Color': champion.student?.house?.house_color,
    'Categories Won': champion.categories_won,
    'Total Points': champion.total_points,
    'Competition Year': champion.competition_year,
    'Gender': champion.gender
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Champions");
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(dataBlob, `champions_${new Date().getFullYear()}.xlsx`);
};

export const exportChampionsToCSV = (champions: Champion[]) => {
  const data = champions.map(champion => ({
    'Name': `${champion.student?.first_name} ${champion.student?.last_name}`,
    'Tamil Name': `${champion.student?.first_name_tamil || ''} ${champion.student?.last_name_tamil || ''}`,
    'House': champion.student?.house?.house_name,
    'House Color': champion.student?.house?.house_color,
    'Categories Won': champion.categories_won,
    'Total Points': champion.total_points,
    'Competition Year': champion.competition_year,
    'Gender': champion.gender
  }));

  const csv = Papa.unparse(data);
  const csvBlob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(csvBlob, `champions_${new Date().getFullYear()}.csv`);
};

export const exportChampionsToPDF = async (champions: Champion[]) => {
  try {
    // Get school logo if available
    const logoUrl = await getSchoolLogo();
    
    let logoImage = null;
    if (logoUrl) {
      try {
        // Fetch the image and convert to base64
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        logoImage = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error loading logo image:', error);
      }
    }
    
    const docDefinition: TDocumentDefinitions = {
      pageOrientation: 'landscape',
      footer: function(currentPage, pageCount) {
        return {
          text: `Side ${currentPage} av ${pageCount}`,
          alignment: 'right',
          margin: [0, 0, 40, 0]
        };
      },
      content: [
        {
          columns: [
            // Left column for logo
            {
              width: 100,
              stack: logoImage ? [
                {
                  image: logoImage as string,
                  width: 80,
                  alignment: 'left'
                }
              ] : [],
              margin: [0, 0, 10, 0]
            },
            // Right column for title
            {
              width: '*',
              text: { text: "சாம்பியன்கள் பட்டியல்", font: 'NotoSansTamil' },
              style: 'header',
              alignment: 'center'
            },
            // Empty column for balance
            {
              width: 100,
              text: ''
            }
          ]
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', '*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'பெயர் (ஆங்கிலம்)', font: 'NotoSansTamil', bold: true },
                { text: 'பெயர் (தமிழ்)', font: 'NotoSansTamil', bold: true },
                { text: 'இல்லம்', font: 'NotoSansTamil', bold: true },
                { text: 'பிரிவுகள்', font: 'NotoSansTamil', bold: true },
                { text: 'புள்ளிகள்', font: 'NotoSansTamil', bold: true }
              ],
              ...champions.map(champion => [
                `${champion.student?.first_name} ${champion.student?.last_name}`,
                {
                  text: `${champion.student?.first_name_tamil || ''} ${champion.student?.last_name_tamil || ''}`,
                  font: 'NotoSansTamil'
                },
                {
                  text: champion.student?.house?.house_name || '',
                  font: 'NotoSansTamil'
                },
                champion.categories_won.toString(),
                champion.total_points.toString()
              ])
            ]
          }
        }
      ],
      defaultStyle: {
        font: 'NotoSansTamil'
      },
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          margin: [0, 0, 0, 20]
        }
      }
    };

    pdfMake.createPdf(docDefinition).download(`champions_${new Date().getFullYear()}.pdf`);
  } catch (error) {
    console.error('Error generating champions PDF:', error);
  }
};

const splitMixedText = (text: string): Array<{ text: string, font: 'NotoSansTamil' | 'Roboto' }> => {
  // Split text into segments
  const segments = text.split(/([A-Za-z0-9\s:]+)/);
  return segments.map(segment => ({
    text: segment,
    font: /^[A-Za-z0-9\s:]+$/.test(segment) ? 'Roboto' : 'NotoSansTamil'
  })).filter(segment => segment.text.trim() !== '');
};

export const exportCompetitionToPDF = async (competition: Competition, referees: EventReferee[], results: Result[]) => {
  try {
    // Generate QR code for the reference number
    const qrCodeDataUrl = await generateQRCode(competition.reference_number);
    
    // Get school logo if available
    const logoUrl = await getSchoolLogo();
    
    let logoImage = null;
    if (logoUrl) {
      try {
        // Fetch the image and convert to base64
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        logoImage = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error loading logo image:', error);
      }
    }

    // Fetch registered participants
    const { data: registrations } = await supabase
      .from('student_event_registrations')
      .select(`
        *,
        student:students(
          student_id,
          first_name,
          last_name,
          first_name_tamil,
          last_name_tamil,
          reference_number,
          house:houses(
            house_name,
            house_color
          )
        )
      `)
      .eq('event_id', competition.event_id)
      .eq('status', 'registered')
      .order('created_at');

    // Fetch all departments to show Tamil names
    const { data: departments } = await supabase
      .from('departments')
      .select('department_id, name, name_tamil, hidden_in_exports')
      .order('name');
    
    // Filter out hidden departments
    const visibleDepartments = departments
      ?.filter(dept => !dept.hidden_in_exports && dept.name_tamil)
      .map(dept => ({
        text: dept.name_tamil,
        font: 'NotoSansTamil'
      }))
      .reduce((acc, curr, idx, arr) => {
        if (idx === arr.length - 1) return [...acc, curr];
        return [...acc, curr, { text: ', ', font: 'Roboto' }];
      }, [] as any[]);

    const eventNameParts = splitMixedText(competition.event_name);
    const refNumber = splitMixedText(competition.reference_number);

    const docDefinition: TDocumentDefinitions = {
      pageOrientation: 'landscape',
      footer: function(currentPage, pageCount) {
        return {
          text: `Side ${currentPage} av ${pageCount}`,
          alignment: 'right',
          margin: [0, 0, 40, 0]
        };
      },
      content: [
        {
          columns: [
            // Left column for logo
            {
              width: 100,
              stack: logoImage ? [
                {
                  image: logoImage as string,
                  width: 80,
                  alignment: 'left'
                }
              ] : [],
              margin: [0, 0, 10, 0]
            },
            // Center column for header
            {
              width: '*',
              stack: [
                {
                  text: [{
                    text: "அன்னை பூபதி ",
                    font: 'NotoSansTamil'
                  }, {
                    text: "தமிழ்க் கலைக்கூடம்",
                    font: 'NotoSansTamil'
                  }],
                  style: 'header',
                  alignment: 'center'
                },
                {
                  text: visibleDepartments,
                  alignment: 'center',
                  margin: [0, 5, 0, 10]
                },
                {
                  text: eventNameParts,
                  style: 'eventHeader',
                  alignment: 'center',
                  margin: [0, 10, 0, 20]
                }
              ]
            },
            // Right column for QR code
            {
              width: 'auto',
              stack: [
                {
                  image: qrCodeDataUrl,
                  width: 100,
                  alignment: 'right',
                  margin: [0, 0, 0, 10]
                },
                {
                  text: [
                    { text: "குறிப்பு எண்: ", font: 'NotoSansTamil' },
                    ...refNumber
                  ],
                  alignment: 'right',
                  fontSize: 10
                },
                {
                  text: [
                    { text: "சரிபார்ப்பு குறியீடு: ", font: 'NotoSansTamil' },
                    { text: competition.verification_code || '-', font: 'Roboto' }
                  ],
                  alignment: 'right',
                  fontSize: 10,
                  margin: [0, 5, 0, 0]
                }
              ]
            }
          ]
        },
        {
          table: {
            headerRows: 0,
            widths: ['*', '*', '*'],
            body: [
              [
                { text: `ஆண்டு: ${competition.competition_year}`, font: 'NotoSansTamil' },
                { text: `பிரிவு: ${competition.category?.category_name}`, font: 'NotoSansTamil' },
                { text: `வயது பிரிவு: ${competition.age_group?.group_name || ''}`, font: 'NotoSansTamil' }
              ],
              [
                { text: `பாலினம்: ${competition.gender_category === 'M' ? 'ஆண்' : competition.gender_category === 'F' ? 'பெண்' : 'அனைத்தும்'}`, font: 'NotoSansTamil' },
                { text: `வகை: ${competition.is_team_event ? 'குழு நிகழ்வு' : 'தனிநபர் நிகழ்வு'}`, font: 'NotoSansTamil' },
                ''
              ]
            ]
          },
          margin: [0, 20, 0, 20]
        },
        {
          text: { text: 'பங்கேற்பாளர்கள்', font: 'NotoSansTamil' },
          style: 'subheader',
          margin: [0, 20, 0, 10]
        },
        {
          table: {
            headerRows: 1,
            widths: ['auto', '*', '*', 'auto', 'auto', '*'],
            body: [
              [
                { text: 'இடம்', font: 'NotoSansTamil', bold: true },
                { text: 'பெயர் (தமிழ்)', font: 'NotoSansTamil', bold: true },
                { text: 'குறிப்பு எண்', font: 'NotoSansTamil', bold: true },
                { text: 'இல்லம்', font: 'NotoSansTamil', bold: true },
                { text: 'நிலை', font: 'NotoSansTamil', bold: true },
                { text: 'குறிப்புகள்', font: 'NotoSansTamil', bold: true }
              ],
              ...(registrations || []).map((reg, idx) => [
                (idx + 1).toString(),
                {
                  text: reg.student?.first_name_tamil && reg.student?.last_name_tamil ? 
                    `${reg.student.first_name_tamil} ${reg.student.last_name_tamil}` : 
                    `${reg.student?.first_name} ${reg.student?.last_name}`,
                  font: reg.student?.first_name_tamil ? 'NotoSansTamil' : 'Roboto'
                },
                reg.student?.reference_number || '',
                { text: reg.student?.house?.house_name || '', font: 'NotoSansTamil' },
                '',
                ''
              ])
            ]
          }
        },
        {
          text: { text: 'நடுவர்கள்', font: 'NotoSansTamil' },
          style: 'subheader',
          margin: [0, 20, 0, 10]
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', '*', '*'],
            body: [
              [
                { text: 'பொறுப்பு', font: 'NotoSansTamil', bold: true },
                { text: 'பெயர்', font: 'NotoSansTamil', bold: true },
                { text: 'கையொப்பம்', font: 'NotoSansTamil', bold: true }
              ],
              ...referees.map(ref => [
                {
                  text: ref.role === 'head' ? 'தலைமை நடுவர்' :
                        ref.role === 'assistant' ? 'உதவி நடுவர்' :
                        ref.role === 'timekeeper' ? 'நேரம் கணக்கிடுபவர்' : 'புள்ளி கணக்கிடுபவர்',
                  font: 'NotoSansTamil'
                },
                {
                  text: ref.referee?.first_name_tamil && ref.referee?.last_name_tamil ? 
                    `${ref.referee.first_name_tamil} ${ref.referee.last_name_tamil}` : 
                    `${ref.referee?.first_name} ${ref.referee?.last_name}`,
                  font: ref.referee?.first_name_tamil ? 'NotoSansTamil' : 'Roboto'
                },
                ''
              ])
            ]
          }
        },
        {
          text: { text: 'போட்டி முடிந்ததும், தாளை அலுவலகத்தில் ஒப்படைக்கவும்.', font: 'NotoSansTamil' },
          style: 'footer',
          alignment: 'center',
          margin: [0, 30, 0, 0]
        }
      ],
      styles: {
        header: {
          fontSize: 20,
          bold: true,
          margin: [0, 0, 0, 10]
        },
        eventHeader: {
          fontSize: 16,
          bold: true
        },
        subheader: {
          fontSize: 16,
          bold: true,
          margin: [0, 10, 0, 5]
        },
        footer: {
          fontSize: 12,
          bold: true
        }
      }
    };

    pdfMake.createPdf(docDefinition).download(`${competition.reference_number}.pdf`);
  } catch (error) {
    console.error('Error generating competition PDF:', error);
  }
};

export const exportAllCompetitionsToPDF = async (competitions: Competition[]) => {
  try {
    // For each competition, fetch referees and registrations
    const competitionData = await Promise.all(competitions.map(async (competition) => {
      const { data: referees } = await supabase
        .from('event_referees')
        .select(`
          assignment_id,
          event_id,
          referee_id,
          role,
          created_at,
          referee:referees(
            referee_id,
            first_name,
            last_name,
            first_name_tamil,
            last_name_tamil,
            email,
            phone
          )
        `)
        .eq('event_id', competition.event_id)
        .order('role');

      const { data: results } = await supabase
        .from('competition_results')
        .select(`
          *,
          student:students(
            student_id,
            first_name,
            last_name,
            date_of_birth,
            house:houses(*)
          )
        `)
        .eq('event_id', competition.event_id)
        .order('position', { ascending: true, nullsLast: true });

      return {
        competition,
        referees: referees || [],
        results: results || []
      };
    }));

    // Create a PDF for each competition
    for (const { competition, referees, results } of competitionData) {
      await exportCompetitionToPDF(competition, referees, results);
    }
  } catch (error) {
    console.error('Error generating all competition PDFs:', error);
  }
};

export const exportAllResultsToPDF = async (competitions: Competition[]) => {
  try {
    // Get school logo if available
    const logoUrl = await getSchoolLogo();
    
    let logoImage = null;
    if (logoUrl) {
      try {
        // Fetch the image and convert to base64
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        logoImage = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error loading logo image:', error);
      }
    }
    
    // For each competition, fetch results and create a results table
    const competitionData = await Promise.all(competitions.map(async (competition) => {
      const { data: results } = await supabase
        .from('competition_results')
        .select(`
          *,
          student:students(
            student_id,
            first_name,
            last_name,
            first_name_tamil,
            last_name_tamil,
            house:houses(
              house_name,
              house_color
            )
          )
        `)
        .eq('event_id', competition.event_id)
        .order('position', { ascending: true, nullsLast: true });

      return {
        competition,
        results: results || []
      };
    }));

    // Fetch visible departments
    const { data: departments } = await supabase
      .from('departments')
      .select('department_id, name, name_tamil, hidden_in_exports')
      .order('name');
    
    // Filter out hidden departments
    const visibleDepartments = departments
      ?.filter(dept => !dept.hidden_in_exports && dept.name_tamil)
      .map(dept => ({
        text: dept.name_tamil,
        font: 'NotoSansTamil'
      }))
      .reduce((acc, curr, idx, arr) => {
        if (idx === arr.length - 1) return [...acc, curr];
        return [...acc, curr, { text: ', ', font: 'Roboto' }];
      }, [] as any[]) || [];

    const docDefinition: TDocumentDefinitions = {
      pageOrientation: 'landscape',
      footer: function(currentPage, pageCount) {
        return {
          text: `Side ${currentPage} av ${pageCount}`,
          alignment: 'right',
          margin: [0, 0, 40, 0]
        };
      },
      content: [
        {
          columns: [
            // Left column for logo
            {
              width: 100,
              stack: logoImage ? [
                {
                  image: logoImage as string,
                  width: 80,
                  alignment: 'left'
                }
              ] : [],
              margin: [0, 0, 10, 0]
            },
            // Center column for title
            {
              width: '*',
              text: { text: "போட்டி முடிவுகள்", font: 'NotoSansTamil' },
              style: 'header',
              alignment: 'center'
            },
            // Empty column for balance
            {
              width: 100,
              text: ''
            }
          ]
        },
        {
          text: visibleDepartments,
          alignment: 'center',
          margin: [0, 5, 0, 20]
        },
        ...competitionData.flatMap(({ competition, results }) => [
          {
            text: splitMixedText(competition.event_name),
            style: 'eventHeader',
            alignment: 'center',
            margin: [0, 20, 0, 10]
          },
          {
            columns: [
              {
                width: '*',
                text: [
                  { text: "குறிப்பு எண்: ", font: 'NotoSansTamil' },
                  ...splitMixedText(competition.reference_number)
                ],
                margin: [0, 0, 0, 5]
              },
              {
                width: '*',
                text: [
                  { text: "சரிபார்ப்பு குறியீடு: ", font: 'NotoSansTamil' },
                  { text: competition.verification_code || '-', font: 'Roboto' }
                ],
                alignment: 'right',
                margin: [0, 0, 0, 5]
              }
            ]
          },
          {
            table: {
              headerRows: 1,
              widths: ['auto', '*', 'auto', 'auto'],
              body: [
                [
                  { text: 'இடம்', font: 'NotoSansTamil', bold: true },
                  { text: 'பெயர் (தமிழ்)', font: 'NotoSansTamil', bold: true },
                  { text: 'இல்லம்', font: 'NotoSansTamil', bold: true },
                  { text: 'புள்ளிகள்', font: 'NotoSansTamil', bold: true }
                ],
                ...results.map(result => [
                  result.position?.toString() || { text: 'பங்கேற்பு', font: 'NotoSansTamil' },
                  {
                    text: result.student?.first_name_tamil && result.student?.last_name_tamil ?
                      `${result.student.first_name_tamil} ${result.student.last_name_tamil}` :
                      `${result.student?.first_name} ${result.student?.last_name}`,
                    font: result.student?.first_name_tamil ? 'NotoSansTamil' : 'Roboto'
                  },
                  {
                    text: result.student?.house?.house_name || '',
                    font: 'NotoSansTamil'
                  },
                  result.points_earned.toString()
                ])
              ]
            },
            margin: [0, 0, 0, 20]
          }
        ])
      ],
      defaultStyle: {
        font: 'NotoSansTamil'
      },
      styles: {
        header: {
          fontSize: 20,
          bold: true,
          margin: [0, 0, 0, 20]
        },
        eventHeader: {
          fontSize: 16,
          bold: true
        }
      }
    };

    pdfMake.createPdf(docDefinition).download('competition_results.pdf');
  } catch (error) {
    console.error('Error generating all results PDF:', error);
  }
};